import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { parishService } from '../services';
import { useToast } from '../hook/use-toast';

const PagarmeSplitStatusPanel: React.FC = () => {
  const { toast } = useToast();
  const [providerStatusByParish, setProviderStatusByParish] = useState<
    Record<string, { providerStatus?: string | null; providerReason?: string | null; checkedAt?: string }>
  >({});

  const splitOverviewQuery = useQuery({
    queryKey: ['pagarme-split-overview'],
    queryFn: () => parishService.getPagarmeSplitOverview(),
    refetchOnWindowFocus: false,
  });

  const checkProviderStatusMutation = useMutation({
    mutationFn: (parishId: string) => parishService.getPagarmeRecipientStatus(parishId),
    onSuccess: (res) => {
      const d = res.data;
      if (!d?.parishId) return;
      setProviderStatusByParish((prev) => ({
        ...prev,
        [d.parishId]: {
          providerStatus: d.providerStatus ?? null,
          providerReason: d.providerReason ?? null,
          checkedAt: d.checkedAt,
        },
      }));
      toast({ title: `Status consultado: ${d.providerStatus || '—'}` });
    },
    onError: (e: any, parishId: string) => {
      const msg =
        e.response?.data?.data?.providerMessage ||
        e.response?.data?.message ||
        e.message ||
        'Erro ao consultar status';
      setProviderStatusByParish((prev) => ({
        ...prev,
        [parishId]: {
          providerStatus: 'erro_consulta',
          providerReason: String(msg),
          checkedAt: new Date().toISOString(),
        },
      }));
      toast({ title: 'Falha ao consultar status no Pagar.me', description: String(msg), variant: 'destructive' });
    },
  });

  const splitRows = splitOverviewQuery.data?.data?.items || [];

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        Status de split por paróquia
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        Visão do cadastro local. Clique em <strong>Consultar status</strong> para buscar no Pagar.me quando necessário.
        Para editar dados do recebedor, use <strong>Revisar</strong> ou a aba Aprovação recebedor.
      </Typography>
      {splitOverviewQuery.isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : splitOverviewQuery.isError ? (
        <Alert severity="error">Não foi possível carregar a visão de split.</Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Paróquia</TableCell>
              <TableCell>Documento</TableCell>
              <TableCell>Recipient (local)</TableCell>
              <TableCell>Status local</TableCell>
              <TableCell>Status Pagar.me</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {splitRows.map((row) => {
              const pid = String(row._id);
              const provider = providerStatusByParish[pid];
              const hasRecipient = !!String(row.pagarMeRecipientId || '').trim();
              const providerText =
                provider?.providerStatus ||
                String((row as any).providerStatus || '') ||
                (hasRecipient ? 'não consultado' : 'sem recipient');
              const reasonText = provider?.providerReason || String((row as any).providerStatusReason || '');
              const checkedAtRaw = provider?.checkedAt || String((row as any).providerStatusCheckedAt || '');
              return (
                <TableRow key={pid} hover>
                  <TableCell>{String(row.name || '—')}</TableCell>
                  <TableCell>
                    {String(row.cpfCnpjType || '').toUpperCase()} {String(row.cpfCnpj || '—')}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 260, wordBreak: 'break-all' }}>
                    {hasRecipient ? String(row.pagarMeRecipientId) : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.recipientSynced ? 'ready' : String(row.onboardingStatus || 'draft')}
                      color={row.recipientSynced ? 'success' : 'default'}
                      variant={row.recipientSynced ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320, wordBreak: 'break-word' }}>
                    <Chip
                      size="small"
                      label={providerText}
                      color={
                        providerText === 'active' ? 'success' : providerText === 'refused' ? 'error' : 'default'
                      }
                      variant="outlined"
                    />
                    {reasonText ? (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        {reasonText}
                      </Typography>
                    ) : null}
                    {checkedAtRaw ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        verificado em {new Date(checkedAtRaw).toLocaleString('pt-BR')}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" justifyContent="flex-end" gap={1} flexWrap="wrap">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => checkProviderStatusMutation.mutate(pid)}
                        disabled={!hasRecipient || checkProviderStatusMutation.isPending}
                      >
                        {checkProviderStatusMutation.isPending ? 'Consultando…' : 'Consultar status'}
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        component={RouterLink}
                        to={`/parishes/pagarme/${encodeURIComponent(pid)}`}
                      >
                        Revisar
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
};

export default PagarmeSplitStatusPanel;
